<script>
  import {onMount, tick} from 'svelte';
  import GanttHelper from './GanttHelper.js';
  import CalendarBlock from './CalendarBlock.svelte';
  import { get } from "svelte/store";

  export let GanttStore

  let startDate;
  let endDate;
  let spanInSeconds;

  let validStartDate;
  let validEndDate;

  let scaleUnits;
  let processingUnit;

  const {
    StartDayDisplayTime,
    EndDayDisplayTime,
    DayStartTime,
    OtherDemarcationTimes,
    CurrentDisplayedEndDateInclDaysOff,
    GetAdjustedDateInclDaysOff,
    SkipDaysOffForwards,
    SkipDaysOffBackwards,
    HolidayDates,
  } = GanttStore;

  $: FindOriginalTimeFromAdjusted = (stamp) => GanttHelper._gantHelper_getCalendarHourStamp(stamp, $StartDayDisplayTime, $EndDayDisplayTime, $DayStartTime);



  // Format (Sorted by timestamp)
  /*
  ** {
  **  StartX: 5.5,
  **  EndX: 21,
  **  TextUp: "3",
  **  TextDown: "Tue", // If null, TextUp is centered
  **  BlockColor: "red",
  ** }
  */
  let calendarUpperBlocks = [];
  let calendarLowerBlocks = [];

  let blockRefs = [];

  $: $CurrentDisplayedEndDateInclDaysOff, ReloadCalendarBlocksDeferred();

  async function ReloadCalendarBlocksDeferred() {
    await tick();

    requestAnimationFrame(() => {
      ReloadCalendarBlocks();
    });
  }


  const ReloadCalendarBlocks = () => {
    if (processingUnit === undefined) return;

    let tmpcalendarUpperBlocks = [];
    let tmpcalendarLowerBlocks = [];

    for (let i = 0; i < 2; i++) {
      const unit = processingUnit[i];
      let timestamp = startDate;
      let adjustedEndDate = $CurrentDisplayedEndDateInclDaysOff;
      let _is_primary = true;
      let blockNb = 0;
      while (timestamp < adjustedEndDate) {
        const _text_visible = blockRefs[blockNb]?.textVisible ?? true;

        const adjustedTimestamp = $GetAdjustedDateInclDaysOff(timestamp);
        const next = GanttHelper.GetNextDateGraduation(timestamp, unit);
        const text = GanttHelper.GetDateCalendarText(FindOriginalTimeFromAdjusted, timestamp, unit, i === 0, !_is_primary && _text_visible);
        const _isDayOff = unit === "D" && $HolidayDates.some((holiday) => new Date(holiday).setHours(0, 0, 0, 0) == new Date(timestamp).setHours(0, 0, 0, 0));
        const color = GanttHelper.GetDateCalendarBlockColor(timestamp, unit, _isDayOff);
        const _sX = 100 * GanttHelper._gantHelper_scale(adjustedTimestamp.getTime(), endDate.getTime(), startDate.getTime());
        const _eX = 100 * GanttHelper._gantHelper_scale(Math.min($GetAdjustedDateInclDaysOff(next).getTime(), endDate.getTime()), endDate.getTime(), startDate.getTime());

        if (Math.abs(_sX - _eX) > 0.0001) {
          const insertable = {
            StartX: _sX,
            EndX: _eX,
            TextUp: text[0],
            TextDown: text[1],
            BlockColor: color,
          }

          if (i === 0) tmpcalendarUpperBlocks.push(insertable);
          else tmpcalendarLowerBlocks.push(insertable);
        }

        timestamp = next;
        if (_text_visible) _is_primary = false;
        blockNb++;
      }
    }
    calendarUpperBlocks = tmpcalendarUpperBlocks;
    calendarLowerBlocks = tmpcalendarLowerBlocks;
    GanttStore.InternalLowerSplits.update((_) => calendarLowerBlocks);
    ReloadSubSplits();
  };

  const ReloadSubSplits = () => {
    const timePoints = [];
    const currentDate = new Date(startDate);
    const displaySize = Math.abs(startDate - endDate);
    const hideSplitsRange = 1 * GanttHelper.GHMONTH;
    const hideSubSplitsRange = 1 * GanttHelper.GHWEEK;

    if (displaySize < 1000 * hideSplitsRange) {
        const rHour = (h) => Math.round(h * 2) / 2;
        const _spl = $OtherDemarcationTimes;
        const kugiri = [GanttHelper._gantHelper_hourToPercentageOfTheDay($DayStartTime) * 24 / 100,
          ...((displaySize < 1000 * hideSubSplitsRange)
           ? _spl.map((ot) => GanttHelper._gantHelper_hourToPercentageOfTheDay(ot) * 24 / 100)
           : [])
         ];

        let _dp2 = 2;
        while (currentDate <= endDate || _dp2 > 0) {
          kugiri.forEach((hour) => timePoints.push(
            new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate(),
              rHour(hour),
              (rHour(hour) * 10 % 10 === 5) ? 30 : 0,
              0
            )));
          currentDate.setDate(currentDate.getDate() + 1);
          if (currentDate > endDate) _dp2--;
        }
    }

    const unique = src => [...new Set(src)];
    GanttStore.InternalLowerSubSplits.update((_) => unique(timePoints));
  }

  onMount(() => {
    GanttStore.CurrentDisplayedEndDate.subscribe(
      (newDate) => {
        endDate = newDate;
        ReloadCalendarBlocksDeferred();
    });
    GanttStore.CurrentDisplayedStartDate.subscribe(
      (newDate) => {
        startDate = newDate;
    });
    GanttStore.ValidEndDate.subscribe(
      (newDate) => {
        validEndDate = newDate;
    });
    GanttStore.ValidStartDate.subscribe(
      (newDate) => {
        validStartDate = newDate;
    });
    GanttStore.DisplayedSpanInSeconds.subscribe(
      (newSpan) => {
        spanInSeconds = newSpan;
        scaleUnits = GanttHelper.GetScaleUnit(newSpan);
        processingUnit = GanttHelper.GetProcessingUnit(scaleUnits);
        ReloadCalendarBlocksDeferred();
    });
  });

  const ScrollTime = (e) => {
    // "event" can be empty for some reason
    if (!e) return

    e.preventDefault();

    if (e.ctrlKey) {
      const currentDisplayedSpanInSeconds = get(GanttStore.DisplayedSpanInSeconds)
      let newDisplayedSpanInSeconds
      // Zoom
      if (e.deltaY > 0) {
        // If we want a more strict restriction use the following code. Currently, I think it's unnecessary
        // GanttStore.DisplayedSpanInSeconds.update((d) => Math.min(d * 1.05, GanttHelper.GHYEAR, (validEndDate.getTime() - validStartDate.getTime()) / 1000));
        newDisplayedSpanInSeconds = Math.min(currentDisplayedSpanInSeconds * 1.05, GanttHelper.GHYEAR)
      } else if (e.deltaY < 0) {
        newDisplayedSpanInSeconds = Math.max(currentDisplayedSpanInSeconds / 1.05, GanttHelper.GHSECOND)
      }
      GanttStore.DisplayedSpanInSeconds.set(newDisplayedSpanInSeconds)
    }
    else {
      // Move
      const currentDisplayedStartDate = get(GanttStore.CurrentDisplayedStartDate)
      let newCurrentDisplayedStartDate
      if (e.deltaY > 0) {
        if (isNaN(validEndDate.getTime())) {
          newCurrentDisplayedStartDate = new Date(currentDisplayedStartDate.getTime() + (endDate.getTime() - currentDisplayedStartDate.getTime()) / 48);
        } else {
          newCurrentDisplayedStartDate = new Date(Math.min(validEndDate.getTime() - spanInSeconds * 1000, currentDisplayedStartDate.getTime() + (endDate.getTime() - currentDisplayedStartDate.getTime()) / 48));
        }
        if (newCurrentDisplayedStartDate < currentDisplayedStartDate) {
          newCurrentDisplayedStartDate = currentDisplayedStartDate;
        }
        GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffForwards(newCurrentDisplayedStartDate));
      }
      else if (e.deltaY < 0) {
        if (isNaN(validStartDate.getTime())) {
          newCurrentDisplayedStartDate = new Date(currentDisplayedStartDate.getTime() - (endDate.getTime() - currentDisplayedStartDate.getTime()) / 48);
        } else {
          newCurrentDisplayedStartDate = new Date(Math.max(validStartDate.getTime(), currentDisplayedStartDate.getTime() - (endDate.getTime() - currentDisplayedStartDate.getTime()) / 48))
        }
        if (newCurrentDisplayedStartDate > currentDisplayedStartDate) {
          newCurrentDisplayedStartDate = currentDisplayedStartDate;
        }
        GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffBackwards(newCurrentDisplayedStartDate));
      }
    }
  }

</script>

<div
  id="gantt_chartarea_calendarheader"
  on:wheel={ScrollTime}
>
  <svg id="gantt_chartarea_calendarsvg">
    {#each calendarUpperBlocks as upperBlock, i}
      <CalendarBlock
        StartY=2
        Item={upperBlock}
        IsUpper={true}
        bind:this={blockRefs[i]}
      />
    {/each}
    {#each calendarLowerBlocks as lowerBlock}
      <CalendarBlock
        StartY=50
        Item={lowerBlock}
        IsUpper={false}
      />
    {/each}
    <line
      x1="0"
      x2="100%"
      y1="50%"
      y2="50%"
      stroke="#888888"
      stroke-width="2"
    />
  </svg>
</div>

<style>
  div#gantt_chartarea_calendarheader {
    width: 100%;
    height: 80px;
  }

  svg#gantt_chartarea_calendarsvg {
    border-bottom: 2px solid #888888;
    margin: 0;
    padding: 0;
    width: 100%;
    height: 80px;
    position: relative;
  }
</style>
